import type { QueryClient } from '@tanstack/react-query';

/**
 * Sammelt Neulade-Anforderungen und fuehrt sie gebuendelt aus.
 *
 * Hintergrund: Inbox, Kundenliste und das Badge in der Seitenleiste haengen an
 * Realtime-Kanaelen. Bisher loeste *jede* eingehende Nachricht in *jeder*
 * offenen Sitzung sofort ein Neuladen aus -- und zwar zweimal, weil zwei
 * Kanaele (conversations und messages) dieselbe Abfrage anstossen.
 *
 * Bei M&M Hairdesign kommen die Nachrichten in Schueben (gemessen bis zu 60 in
 * einer Stunde, sonst 2-21). Zehn Nachrichten kurz hintereinander bedeuteten
 * zehn komplette Neuladungen pro Sitzung. Mit Buendelung wird daraus eine.
 *
 * Bewusst kein sofortiger erster Aufruf: Die Ereignisse kommen paarweise
 * (zwei Kanaele pro Nachricht), ein Vorab-Aufruf wuerde die Haelfte der
 * Ersparnis wieder auffressen.
 */
export const STANDARD_BUENDELUNG_MS = 2000;

export interface Buendler {
  /** Fordert ein Neuladen an. Mehrfachaufrufe innerhalb des Fensters ergeben einen Lauf. */
  anfordern: (queryKey: readonly unknown[]) => void;
  /** Alle ausstehenden Laeufe verwerfen (beim Abbauen der Komponente). */
  abbrechen: () => void;
}

export function createInvalidationBuendler(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  delayMs: number = STANDARD_BUENDELUNG_MS,
  scheduler: {
    setTimeout: (fn: () => void, ms: number) => number;
    clearTimeout: (id: number) => void;
  } = {
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (id) => window.clearTimeout(id),
  }
): Buendler {
  const laufende = new Map<string, number>();

  return {
    anfordern(queryKey) {
      const kennung = JSON.stringify(queryKey);
      const vorheriger = laufende.get(kennung);
      if (vorheriger !== undefined) scheduler.clearTimeout(vorheriger);

      const id = scheduler.setTimeout(() => {
        laufende.delete(kennung);
        queryClient.invalidateQueries({ queryKey: queryKey as unknown[] });
      }, delayMs);

      laufende.set(kennung, id);
    },

    abbrechen() {
      laufende.forEach((id) => scheduler.clearTimeout(id));
      laufende.clear();
    },
  };
}
